import clsx from 'clsx';
import React, { useEffect, useRef } from 'react';

const isKeyboardEvent = (event: Event): event is KeyboardEvent => 'key' in event;

const mergeRefs =
  <T,>(...refs: (React.Ref<T> | null | undefined)[]) =>
  (node: T | null) => {
    for (const ref of refs) {
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    }
  };

const normalizeKey = (key: string): string => {
  const lowerKey = key.toLowerCase();

  switch (lowerKey) {
    case ' ':
    case 'spacebar': // for older browsers
      return 'space';

    case 'arrowup':
      return 'up';
    case 'arrowdown':
      return 'down';
    case 'arrowleft':
      return 'left';
    case 'arrowright':
      return 'right';

    case 'esc':
    case 'escape':
      return 'escape';

    default:
      return lowerKey;
  }
};

/**
 * Modifier keys are matched against the event's modifier flags rather
 * than the held-key set, so chords name them with these tokens.
 */
const MODIFIERS = ['alt', 'ctrl', 'meta', 'shift'] as const;

const isModifier = (key: string): key is (typeof MODIFIERS)[number] =>
  (MODIFIERS as readonly string[]).includes(key);

/**
 * In case of multiple hotkeys matching the pressed key(s),
 * the hotkey with a higher priority is selected. All others
 * are ignored.
 */
const hotkeyPriority = { singleKey: 0, combo: 1, sequence: 2, chord: 3 } as const;

/**
 * This type of function receives a keyboard event and an array of
 * previously pressed keys (within the last second), and returns
 * `isMatch` (whether the pressed keys match a hotkey) and `priority`
 * (a weighting used to resolve conflicts when two hotkeys match the
 * pressed keys)
 */
type MatcherContext = {
  /** Keys pressed in order within the last second, used for sequences. */
  bufferedKeys: string[];
  /** Keys currently held down, used for chords pressed at once. */
  pressedKeys: Set<string>;
};

type KeyMatcher = (
  event: KeyboardEvent,
  context: MatcherContext,
) => {
  /**
   * Whether the event.key matches the hotkey
   */
  isMatch: boolean;
  /**
   * If there are multiple matching hotkeys, the
   * first one with the highest priority will be handled
   */
  priority: (typeof hotkeyPriority)[keyof typeof hotkeyPriority];
};

/**
 * Matches a single key
 */
function just(keyName: string): KeyMatcher {
  return (event) => ({
    isMatch:
      normalizeKey(event.key) === keyName && !event.altKey && !event.ctrlKey && !event.metaKey,
    priority: hotkeyPriority.singleKey,
  });
}

/**
 * Matches any single key out of those provided
 */
function any(...keys: string[]): KeyMatcher {
  return (event, context) => ({
    isMatch: keys.some((keyName) => just(keyName)(event, context).isMatch),
    priority: hotkeyPriority.singleKey,
  });
}

/**
 * Matches a single key combined with the option/alt modifier
 */
function optionPlus(key: string): KeyMatcher {
  return (event) => ({
    // Matching against event.code here as alt combos are often
    // mapped to other characters
    isMatch: event.altKey && event.code === `Key${key.toUpperCase()}`,
    priority: hotkeyPriority.combo,
  });
}

/** Matches a single key combined with the shift modifier */
function shiftPlus(key: string): KeyMatcher {
  return (event) => ({
    isMatch: event.shiftKey && normalizeKey(event.key) === key,
    priority: hotkeyPriority.combo,
  });
}

/**
 * Matches when all of the given keys are held down at the same time.
 * Modifier tokens (alt/ctrl/meta/shift) are matched against the
 * event's modifier flags; every other key must currently be held.
 */
function chord(...keys: string[]): KeyMatcher {
  const requiredModifiers = keys.filter(isModifier);
  const requiredKeys = keys.filter((key) => !isModifier(key)).map(normalizeKey);

  return (event, { pressedKeys }) => {
    const modifiersMatch =
      requiredModifiers.includes('alt') === event.altKey &&
      requiredModifiers.includes('ctrl') === event.ctrlKey &&
      requiredModifiers.includes('meta') === event.metaKey &&
      requiredModifiers.includes('shift') === event.shiftKey;

    const keysMatch = requiredKeys.every((key) => pressedKeys.has(key));

    return {
      isMatch: modifiersMatch && keysMatch,
      priority: hotkeyPriority.chord,
    };
  };
}

/**
 * Matches when all provided keys are pressed in sequence.
 */
function sequence(...sequence: string[]): KeyMatcher {
  return (event, { bufferedKeys }) => {
    const lastKeyInSequence = sequence.at(-1);
    const startOfSequence = sequence.slice(0, -1);
    const relevantBufferedKeys = bufferedKeys?.slice(-startOfSequence.length);

    const bufferMatchesStartOfSequence =
      !!relevantBufferedKeys && startOfSequence.join('') === relevantBufferedKeys.join('');

    return {
      isMatch: bufferMatchesStartOfSequence && normalizeKey(event.key) === lastKeyInSequence,
      priority: hotkeyPriority.sequence,
    };
  };
}

/**
 * This is a map of all global hotkeys we support.
 * To trigger a hotkey, a handler with a matching name must be
 * provided to the `useHotkeys` hook or `Hotkeys` component.
 */
const hotkeyMatcherMap = {
  help: just('?'),
  search: any('s', '/'),
  back: just('backspace'),
  new: just('n'),
  forceNew: optionPlus('n'),
  focusColumn: any('1', '2', '3', '4', '5', '6', '7', '8', '9'),
  focusLastColumn: just('0'),
  focusPreviousColumn: just('left'),
  focusNextColumn: just('right'),
  shrinkColumn: chord('shift', 'r', 'left'),
  widenColumn: chord('shift', 'r', 'right'),
  moveColumnLeft: shiftPlus('left'),
  moveColumnRight: shiftPlus('right'),
  switchToPreviousLayout: shiftPlus('up'),
  switchToNextLayout: shiftPlus('down'),
  // focusLoadMore: just('l'),
  reply: just('r'),
  favourite: just('f'),
  boost: just('b'),
  quote: just('q'),
  mention: just('m'),
  react: just('e'),
  open: any('enter', 'o'),
  openProfile: just('p'),
  moveDown: any('down', 'j'),
  moveUp: any('up', 'k'),
  toggleHidden: just('x'),
  toggleSensitive: just('h'),
  // toggleComposeSpoilers: optionPlus('x'),
  openMedia: just('a'),
  // onTranslate: just('t'),
  goToHome: sequence('g', 'h'),
  goToNotifications: sequence('g', 'n'),
  // goToLocal: sequence('g', 'l'),
  // goToFederated: sequence('g', 't'),
  // goToDirect: sequence('g', 'd'),
  // goToStart: sequence('g', 's'),
  goToFavourites: sequence('g', 'f'),
  // goToPinned: sequence('g', 'p'),
  goToProfile: sequence('g', 'u'),
  goToBlocked: sequence('g', 'b'),
  goToMuted: sequence('g', 'm'),
  goToRequests: sequence('g', 'r'),
  // cheat: sequence(
  //   'up',
  //   'up',
  //   'down',
  //   'down',
  //   'left',
  //   'right',
  //   'left',
  //   'right',
  //   'b',
  //   'a',
  //   'enter',
  // ),
  addColumn: just('c'),
  columnBack: just('escape'),
} as const;

type HotkeyName = keyof typeof hotkeyMatcherMap;

type HandlerMap = Partial<Record<HotkeyName, (event: KeyboardEvent) => void | boolean>>;

function useHotkeys<T extends HTMLElement>(handlers: HandlerMap) {
  const ref = useRef<T>(null);
  const bufferedKeys = useRef<string[]>([]);
  const pressedKeys = useRef<Set<string>>(new Set());
  const sequenceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  /**
   * Store the latest handlers object in a ref so we don't need to
   * add it as a dependency to the main event listener effect
   */
  const handlersRef = useRef(handlers);
  useEffect(() => {
    handlersRef.current = handlers;
  }, [handlers]);

  useEffect(() => {
    const element = ref.current ?? document;

    const DRIVE_KEYS = ['up', 'down', 'left', 'right', 'home', 'end', 'pageup', 'pagedown'];

    function listener(event: Event) {
      if (isKeyboardEvent(event)) {
        pressedKeys.current.add(normalizeKey(event.key));
      }

      // Ignore key presses from input, textarea, or select elements
      const tagName = (event.target as HTMLElement).tagName.toLowerCase();
      const shouldHandleEvent =
        isKeyboardEvent(event) &&
        !event.defaultPrevented &&
        !['input', 'textarea', 'select', 'em-emoji-picker'].includes(tagName) &&
        !(event.target as HTMLElement).closest(
          '[contenteditable], .multiselect-container, .dropdown-menu__content',
        ) &&
        !(
          (event.target as HTMLElement)?.closest('.drive-file') &&
          DRIVE_KEYS.includes(normalizeKey(event.key))
        ) &&
        !(['a', 'button'].includes(tagName) && normalizeKey(event.key) === 'enter');

      if (shouldHandleEvent) {
        const matchCandidates: {
          // A candidate will be have an undefined handler if it's matched,
          // but handled in a parent component rather than this one.
          handler: ((event: KeyboardEvent) => void | boolean) | undefined;
          priority: number;
        }[] = [];

        (Object.keys(hotkeyMatcherMap) as HotkeyName[]).forEach((handlerName) => {
          const handler = handlersRef.current[handlerName];
          const hotkeyMatcher = hotkeyMatcherMap[handlerName];

          const { isMatch, priority } = hotkeyMatcher(event, {
            bufferedKeys: bufferedKeys.current,
            pressedKeys: pressedKeys.current,
          });

          if (isMatch) {
            matchCandidates.push({ handler, priority });
          }
        });

        // Sort all matches by priority
        const sortedCandidates = matchCandidates.toSorted((a, b) => b.priority - a.priority);

        const bestMatchingHandler = sortedCandidates.at(0)?.handler;
        if (bestMatchingHandler) {
          const result = bestMatchingHandler(event);
          if (result !== false) {
            event.stopPropagation();
            event.preventDefault();
          }
        }

        // Add last keypress to buffer
        bufferedKeys.current.push(normalizeKey(event.key));

        // Reset the timeout
        if (sequenceTimer.current) {
          clearTimeout(sequenceTimer.current);
        }
        sequenceTimer.current = setTimeout(() => {
          bufferedKeys.current = [];
        }, 1000);
      }
    }
    function keyupListener(event: Event) {
      if (isKeyboardEvent(event)) {
        pressedKeys.current.delete(normalizeKey(event.key));
      }
    }

    function clearPressedKeys() {
      pressedKeys.current.clear();
    }

    element.addEventListener('keydown', listener);
    element.addEventListener('keyup', keyupListener);
    window.addEventListener('blur', clearPressedKeys);

    return () => {
      element.removeEventListener('keydown', listener);
      element.removeEventListener('keyup', keyupListener);
      window.removeEventListener('blur', clearPressedKeys);
      if (sequenceTimer.current) {
        clearTimeout(sequenceTimer.current);
      }
    };
  }, []);

  return ref;
}

interface IHotkeys extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * An object containing functions to be run when a hotkey is pressed.
   * The key must be the name of a registered hotkey, e.g. "help" or "search"
   */
  handlers: HandlerMap;
  /**
   * When enabled, hotkeys will be matched against the document root
   * rather than only inside of this component's DOM node.
   */
  global?: boolean;
  /**
   * Allow the rendered `div` to be focused
   */
  focusable?: boolean;
  children: React.ReactNode;
  element?: React.ComponentType | keyof React.JSX.IntrinsicElements;
}

/**
 * The Hotkeys component allows us to globally register keyboard combinations
 * under a name and assign actions to them, either globally or scoped to a portion
 * of the app.
 *
 * ### How to use
 *
 * To add a new hotkey, add its key combination to the `hotkeyMatcherMap` object
 * and give it a name.
 *
 * Use the `<Hotkeys>` component or the `useHotkeys` hook in the part of of the app
 * where you want to handle the action, and pass in a handlers object.
 *
 * ```tsx
 * <Hotkeys handlers={{open: openStatus}} />
 * ```
 *
 * Now this function will be called when the 'open' hotkey is pressed by the user.
 */
export const Hotkeys = React.forwardRef<HTMLDivElement, IHotkeys>(
  ({ handlers, global, focusable = true, element: Element = 'div', ...props }, forwardedRef) => {
    const ref = useHotkeys<HTMLDivElement>(handlers);

    Element = Element as 'div';

    return (
      <Element
        ref={mergeRefs(global ? undefined : ref, forwardedRef)}
        tabIndex={focusable ? -1 : undefined}
        {...props}
        className={clsx(props.className, focusable && 'focusable')}
      />
    );
  },
);

Hotkeys.displayName = 'Hotkeys';
